import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement} [eventsHolder] - Where the K Event calendar is parked while the Events filter isn't showing it.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * The results tab and content filter the user picked, kept while they keep typing.
   * @type {string | null}
   */
  #activeTab = null;

  /** @type {string} */
  #activeFilter = 'all';

  /**
   * Watches the K Event calendar so the Events pill count can update once the app has built it.
   * @type {MutationObserver | null}
   */
  #eventsObserver = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }

    // K Event builds the calendar some time after page load, possibly after results are already showing
    const { eventsHolder } = this.refs;
    if (eventsHolder) {
      this.#eventsObserver = new MutationObserver(() => this.#updateEventsCount());
      this.#eventsObserver.observe(eventsHolder, { childList: true, subtree: true });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#eventsObserver?.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  /**
   * Result items the arrow keys can move through: only those currently shown,
   * so hidden tabs and filtered-out content are skipped.
   */
  get #allResultsItems() {
    const allItems = Array.from(
      this.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')
    ).filter((item) => item instanceof HTMLElement && item.offsetParent !== null);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Switches between the Products and Content tabs.
   * @param {MouseEvent} event - The click event.
   */
  selectTab(event) {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[data-tab]');
    if (!(tab instanceof HTMLElement) || !tab.dataset.tab) return;

    this.#activeTab = tab.dataset.tab;
    this.#applyTab(this.#activeTab);
  }

  /**
   * Filters the Content tab by resource type.
   * @param {MouseEvent} event - The click event.
   */
  filterContent(event) {
    const filter = /** @type {HTMLElement} */ (event.target).closest('[data-filter]');
    if (!(filter instanceof HTMLElement) || !filter.dataset.filter) return;

    this.#activeFilter = filter.dataset.filter;
    this.#applyFilter(this.#activeFilter);
  }

  /**
   * @param {string} tabName - 'products' or 'content'
   */
  #applyTab(tabName) {
    const results = this.refs.predictiveSearchResults;
    if (!results.querySelector(`[data-tab="${tabName}"]`)) return;

    this.#clearSelection();

    for (const tab of results.querySelectorAll('[data-tab]')) {
      tab.setAttribute('aria-selected', String(tab.getAttribute('data-tab') === tabName));
    }

    for (const element of results.querySelectorAll('[data-panel], [data-tab-link]')) {
      if (!(element instanceof HTMLElement)) continue;
      element.hidden = (element.dataset.panel ?? element.dataset.tabLink) !== tabName;
    }

    results.querySelector('.bst-search__panels')?.scrollTo(0, 0);
  }

  /**
   * The K Event calendar is built once, on page load, in a holder outside the results (they're
   * replaced on every search). The Events filter borrows it: move it into the Content tab's slot to
   * show it, and back to the holder to hide it - always before the results are re-rendered.
   * @param {boolean} show
   */
  #moveEventsCalendar(show) {
    const { eventsHolder } = this.refs;
    const calendar = eventsHolder?.firstElementChild ?? this.querySelector('[data-events-slot] > *');
    if (!eventsHolder || !calendar) return;

    const slot = this.refs.predictiveSearchResults.querySelector('[data-events-slot]');
    if (show && slot instanceof HTMLElement) {
      slot.append(calendar);
      slot.hidden = false;
    } else {
      eventsHolder.append(calendar);
      if (slot instanceof HTMLElement) slot.hidden = true;
    }
  }

  /**
   * Shows how many events the K Event calendar holds on the Events pill (hidden until it has any).
   */
  #updateEventsCount() {
    const calendar = this.refs.eventsHolder?.firstElementChild ?? this.querySelector('[data-events-slot] > *');
    const count = calendar?.querySelectorAll('.k-popup__event-calendar-item').length ?? 0;

    for (const element of this.querySelectorAll('[data-events-count]')) {
      if (!(element instanceof HTMLElement)) continue;
      element.textContent = String(count);
      element.hidden = count === 0;
    }
  }

  /**
   * @param {string} filterName - 'all', 'events', 'page', 'article' or 'collection'
   */
  #applyFilter(filterName) {
    const results = this.refs.predictiveSearchResults;
    const list = results.querySelector('.bst-search__content');
    if (!(list instanceof HTMLElement)) return;

    // Fall back to "All" when the new results have nothing of the picked type
    const filterButtons = Array.from(results.querySelectorAll('.bst-search__filter[data-filter]'));
    if (!filterButtons.some((button) => button.getAttribute('data-filter') === filterName)) {
      filterName = 'all';
    }

    this.#clearSelection();
    list.dataset.filter = filterName;
    this.#moveEventsCalendar(filterName === 'events');

    for (const button of filterButtons) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-filter') === filterName));
    }
  }

  /**
   * Drops the arrow-key selection without moving focus (unlike setting #currentIndex).
   */
  #clearSelection() {
    for (const item of this.querySelectorAll('[ref="resultsItems[]"][aria-selected], .predictive-search-results__card[aria-selected]')) {
      item.removeAttribute('aria-selected');
      item.classList.remove('keyboard-focus');
    }
  }

  /**
   * Re-applies the user's tab and filter after new results are rendered.
   * A tab with no results falls back to the one the server picked.
   */
  #restoreResultsState() {
    const results = this.refs.predictiveSearchResults;

    this.#updateEventsCount();

    if (this.#activeTab) {
      const tab = results.querySelector(`[data-tab="${this.#activeTab}"]`);
      const count = Number(tab?.querySelector('.bst-search__tab-count')?.textContent ?? 0);
      // The events calendar isn't affected by the search, so Content stays open for it even with no content results
      const keepsEvents = this.#activeTab === 'content' && this.#activeFilter === 'events';
      if (tab && (count > 0 || keepsEvents)) this.#applyTab(this.#activeTab);
    }

    if (this.#activeFilter !== 'all') this.#applyFilter(this.#activeFilter);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    // Let focused tabs, filters and links handle their own Enter
    if (event.key === 'Enter' && event.target !== this.refs.searchInput) return;

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Without an explicit type list Shopify only returns products and search suggestions;
    // `query` keeps the suggestions, which fill the Related Searches column
    url.searchParams.set('resources[type]', 'query,product,page,article,collection');
    // Pages, blog posts and collections are only matched on their title unless told otherwise.
    // Products always search every field and ignore this.
    url.searchParams.set('resources[options][fields]', 'title,body,tag,author');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        // Park the events calendar first so the re-render can't throw it away
        this.#moveEventsCalendar(false);
        morph(predictiveSearchResults, resultsMarkup);

        this.#restoreResultsState();
        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.#activeTab = null;
    this.#activeFilter = 'all';
    this.#moveEventsCalendar(false);
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
